import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Mic, Headphones, Volume2, Video, Sun, Brain, Ear, Eye, 
  Activity, ShieldCheck, Lock, Globe, Grid, Layers, FileText, 
  Download, ArrowUpRight, Sparkles, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { AbilityBrandLogo, ClarvosCTAButton } from './ClarvosBrand';

interface GlobalFooterProps {
  onNavigateToTab: (tab: any) => void;
  onSelectModule?: (moduleId: string) => void;
  onSelectDisability?: (clusterId: string) => void;
  onScrollToComparison?: () => void;
}

export const GlobalFooter: React.FC<GlobalFooterProps> = ({
  onNavigateToTab,
  onSelectModule,
  onSelectDisability,
  onScrollToComparison
}) => {
  const { i18n } = useTranslation();
  const [mobileAccordion, setMobileAccordion] = useState<Record<string, boolean>>({
    suites: true,
    wings: true,
    ops: false,
    governance: false
  });

  const toggleAccordion = (key: string) => {
    setMobileAccordion(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const currentYear = new Date().getFullYear();

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('abilityos_lang', lang);
    }
  };

  return (
    <footer className="bg-[#121114] text-white pt-20 pb-28 md:pb-16 px-4 sm:px-8 lg:px-12 rounded-t-[44px] border-t border-white/10 relative mt-20 shadow-2xl overflow-hidden">
      
      {/* Background ambient accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#ffd64e]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Top Header Row: Brand, Tagline & Schedule Walkthrough CTA */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 pb-12 border-b border-white/10">
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-3">
              <AbilityBrandLogo variant="light" size="lg" />
              <span className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-mono text-[#ffd64e] font-bold">
                CLINICAL SUITE v2.4
              </span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              The unified, 100% hardware-free clinical operating system replacing fragmented speech workstations, diagnostic audiometers, and isolated apps for special schools and therapy hospitals worldwide.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ClarvosCTAButton
              text="Schedule Clinical Demo"
              hoverText="Talk to Specialists"
              onClick={() => onNavigateToTab('request_demo')}
              variant="yellow"
              size="md"
            />
            <button
              onClick={() => onNavigateToTab('downloads')}
              className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs border border-white/20 transition-all flex items-center gap-2 cursor-pointer shadow-sm"
            >
              <Download className="w-3.5 h-3.5 text-[#ffd64e]" />
              <span>Download Native Apps (All OS)</span>
            </button>
          </div>
        </div>

        {/* 4 Architectural Columns (Desktop: 4 cols, Tablet: 2x2, Mobile: Collapsible Accordion) */}
        <div className="py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 border-b border-white/10">
          
          {/* =================================================================== */}
          {/* COLUMN 1: CLINICAL SOFTWARE SUITES                                  */}
          {/* =================================================================== */}
          <div className="bg-white/5 rounded-3xl p-6 border border-white/10 shadow-sm flex flex-col justify-between">
            <div>
              <div 
                onClick={() => toggleAccordion('suites')}
                className="flex items-center justify-between cursor-pointer md:cursor-default mb-4"
              >
                <div className="flex items-center gap-2 text-xs font-mono font-black uppercase tracking-wider text-[#ffd64e]">
                  <Mic className="w-4 h-4 text-[#ffd64e]" />
                  <span>Clinical Software Suites</span>
                </div>
                <div className="md:hidden text-slate-400">
                  {mobileAccordion.suites ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {(mobileAccordion.suites || window.innerWidth >= 768) && (
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li>
                    <button 
                      onClick={() => onSelectModule && onSelectModule('speech-therapy')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Mic className="w-3.5 h-3.5 text-[#ffd64e]" />
                        <span className="font-semibold">Dr. Speech Biofeedback</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-[#ffd64e]">Dual Wave</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectModule && onSelectModule('audiometry-hearing')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Headphones className="w-3.5 h-3.5 text-sky-400" />
                        <span className="font-semibold">Pure-Tone Audiometry</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-sky-400">Speech Banana</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onNavigateToTab('aac')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-semibold">AAC Motor Planning</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-emerald-400">Morphology</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectModule && onSelectModule('speech-therapy')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Video className="w-3.5 h-3.5 text-purple-400" />
                        <span className="font-semibold">Digital Articulation Mirror</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-purple-400">WebRTC</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectModule && onSelectModule('sensory-room')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Sun className="w-3.5 h-3.5 text-amber-300" />
                        <span className="font-semibold">Snoezelen Sensory Room</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-amber-300">Calm Gym</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-slate-400 flex items-center justify-between">
              <span>All 5 Clinical Studios</span>
              <span className="text-[#ffd64e] font-bold">100% HW-Free</span>
            </div>
          </div>

          {/* =================================================================== */}
          {/* COLUMN 2: DISABILITY WINGS (RPwD 2016 & DSM-5)                      */}
          {/* =================================================================== */}
          <div className="bg-white/5 rounded-3xl p-6 border border-white/10 shadow-sm flex flex-col justify-between">
            <div>
              <div 
                onClick={() => toggleAccordion('wings')}
                className="flex items-center justify-between cursor-pointer md:cursor-default mb-4"
              >
                <div className="flex items-center gap-2 text-xs font-mono font-black uppercase tracking-wider text-sky-400">
                  <Brain className="w-4 h-4 text-sky-400" />
                  <span>Disability Wings (RPwD)</span>
                </div>
                <div className="md:hidden text-slate-400">
                  {mobileAccordion.wings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {(mobileAccordion.wings || window.innerWidth >= 768) && (
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li>
                    <button 
                      onClick={() => onSelectDisability ? onSelectDisability('autism-neuro') : onNavigateToTab('disabilities')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Brain className="w-3.5 h-3.5 text-pink-400" />
                        <span className="font-semibold">Autism & ADHD Wing</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-pink-400">ABA / Sensory</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectDisability ? onSelectDisability('hearing-deaf') : onNavigateToTab('disabilities')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Ear className="w-3.5 h-3.5 text-sky-400" />
                        <span className="font-semibold">Hearing Impairment & Deaf</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-sky-400">ISL / Audio</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectDisability ? onSelectDisability('speech-language') : onNavigateToTab('disabilities')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-3.5 h-3.5 text-[#ffd64e]" />
                        <span className="font-semibold">Speech & Articulation</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-[#ffd64e]">Apraxia</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectDisability ? onSelectDisability('locomotor-physical') : onNavigateToTab('disabilities')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-semibold">Cerebral Palsy & Physical</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-emerald-400">Switch Dwell</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onSelectDisability ? onSelectDisability('visual-blindness') : onNavigateToTab('disabilities')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 text-amber-400" />
                        <span className="font-semibold">Visual Impairment & Blind</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-amber-400">Braille Hub</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-slate-400 flex items-center justify-between">
              <span>Statutory Compliance</span>
              <span className="text-sky-400 font-bold">RPwD Act 2016</span>
            </div>
          </div>

          {/* =================================================================== */}
          {/* COLUMN 3: ENTERPRISE & SCHOOL OPERATIONS                            */}
          {/* =================================================================== */}
          <div className="bg-white/5 rounded-3xl p-6 border border-white/10 shadow-sm flex flex-col justify-between">
            <div>
              <div 
                onClick={() => toggleAccordion('ops')}
                className="flex items-center justify-between cursor-pointer md:cursor-default mb-4"
              >
                <div className="flex items-center gap-2 text-xs font-mono font-black uppercase tracking-wider text-emerald-400">
                  <Layers className="w-4 h-4 text-emerald-400" />
                  <span>Enterprise & School Ops</span>
                </div>
                <div className="md:hidden text-slate-400">
                  {mobileAccordion.ops ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {(mobileAccordion.ops || window.innerWidth >= 768) && (
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li>
                    <button 
                      onClick={() => onNavigateToTab('modules')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Grid className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-semibold">31-Module Clinical Suite</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-emerald-400">Interactive</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onNavigateToTab('pricing')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Globe className="w-3.5 h-3.5 text-[#ffd64e]" />
                        <span className="font-semibold text-white">Subscription &amp; Pricing</span>
                      </div>
                      <span className="text-[10px] text-[#ffd64e] font-mono group-hover:text-white">PPP Rates</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => {
                        onScrollToComparison ? onScrollToComparison() : onNavigateToTab('landing');
                        setTimeout(() => {
                          const el = document.getElementById('comparison');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }, 150);
                      }}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-[#ffd64e]" />
                        <span className="font-semibold">Clinical Comparison Matrix</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-[#ffd64e]">TCO Chart</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onNavigateToTab('school_admin')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5 text-purple-400" />
                        <span className="font-semibold">Treatment Audit Tracker</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-purple-400">Live EHR</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onNavigateToTab('downloads')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Download className="w-3.5 h-3.5 text-sky-400" />
                        <span className="font-semibold">Native Apps & PWA Suite</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-sky-400">All 31 Modules</span>
                    </button>
                  </li>

                  <li>
                    <button 
                      onClick={() => onNavigateToTab('school_admin')}
                      className="w-full text-left py-1.5 px-2.5 rounded-xl hover:bg-white/10 hover:text-white transition flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-emerald-300" />
                        <span className="font-semibold">IEP PDF Clinical Exporter</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono group-hover:text-emerald-300">1-Click</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-slate-400 flex items-center justify-between">
              <span>School District License</span>
              <span className="text-emerald-400 font-bold">Unlimited Seats</span>
            </div>
          </div>

          {/* =================================================================== */}
          {/* COLUMN 4: CLINICAL GOVERNANCE & COMPLIANCE STANDARDS                */}
          {/* =================================================================== */}
          <div className="bg-white/5 rounded-3xl p-6 border border-white/10 shadow-sm flex flex-col justify-between">
            <div>
              <div 
                onClick={() => toggleAccordion('governance')}
                className="flex items-center justify-between cursor-pointer md:cursor-default mb-4"
              >
                <div className="flex items-center gap-2 text-xs font-mono font-black uppercase tracking-wider text-purple-400">
                  <ShieldCheck className="w-4 h-4 text-purple-400" />
                  <span>Clinical Governance</span>
                </div>
                <div className="md:hidden text-slate-400">
                  {mobileAccordion.governance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {(mobileAccordion.governance || window.innerWidth >= 768) && (
                <div className="space-y-3 text-xs font-mono text-slate-300">
                  
                  <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div>
                      <div className="font-bold text-white text-[11px]">HIPAA & FERPA Verified</div>
                      <div className="text-[10px] text-slate-400">Zero-Knowledge Telemetry Encryption</div>
                    </div>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#ffd64e] shrink-0" />
                    <div>
                      <div className="font-bold text-white text-[11px]">WCAG 2.2 AAA Accessible</div>
                      <div className="text-[10px] text-slate-400">Section 508 & ADA Level Compliant</div>
                    </div>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                    <Lock className="w-4 h-4 text-sky-400 shrink-0" />
                    <div>
                      <div className="font-bold text-white text-[11px]">Dexie.js Offline Persistence</div>
                      <div className="text-[10px] text-slate-400">Local Cache with Zero Data Loss</div>
                    </div>
                  </div>

                </div>
              )}
            </div>

            {/* Live Operational System Telemetry Badge */}
            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] font-mono">
              <span className="text-slate-400">System Telemetry:</span>
              <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                <span>All Systems Operational</span>
              </span>
            </div>
          </div>

        </div>

        {/* Bottom Bar: Interactive Language Switcher & Legal Navigation */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-slate-400 font-mono">
          
          {/* Interactive Multi-Language Switcher */}
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-bold flex items-center gap-1">
              <Globe className="w-3.5 h-3.5 text-[#ffd64e]" />
              <span>Language:</span>
            </span>
            <div className="flex items-center gap-1 bg-white/10 p-1 rounded-full border border-white/15">
              {[
                { code: 'en', label: 'English' },
                { code: 'hi', label: 'हिन्दी' },
                { code: 'mr', label: 'मराठी' }
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                    i18n.language === lang.code
                      ? 'bg-[#ffd64e] text-black shadow-sm font-black'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Copyright Notice */}
          <div className="text-center md:text-left">
            Copyright © {currentYear} AbilityOS Technologies Inc. All clinical models & algorithms reserved.
          </div>

          {/* Legal & Founder Links */}
          <div className="flex items-center gap-5">
            <button 
              onClick={() => onNavigateToTab('request_demo')} 
              className="hover:text-white transition-colors cursor-pointer"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => onNavigateToTab('request_demo')} 
              className="hover:text-white transition-colors cursor-pointer"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => onNavigateToTab('founder')} 
              className="text-[#ffd64e] hover:underline font-bold cursor-pointer flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3 fill-current" />
              <span>Founder Cockpit</span>
            </button>
          </div>

        </div>

      </div>
    </footer>
  );
};
